// frontend/src/components/auth/RegisterForm.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Lock, Mail, Eye, EyeOff, Car, UserCheck,
  AlertCircle, Globe, Truck, ChevronDown
} from 'lucide-react';
import { useAuth } from './AuthProvider';
import AuthLayout from './AuthLayout';
import { trackEvent } from '../../lib/metaPixel';
import { captureSignupSource, recordSignupComplete } from '../../services/signupAttribution';

// Country Selector Component
const CountrySelector = ({ value, onChange, error = null }) => {
  const countries = [
    { code: 'US', name: 'United States', flag: '🇺🇸', region: 'US' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', region: 'CA' },
  ];

  return (
    <div className="space-y-1.5">
      <label htmlFor="country" className="block text-small font-semibold text-ink-soft">
        Country/Region
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Globe className="h-5 w-5 text-ink-muted" />
        </div>
        <select
          id="country"
          value={value}
          onChange={(e) => {
            const country = countries.find(c => c.code === e.target.value);
            onChange(country);
          }}
          className={`w-full min-h-touch pl-10 pr-10 rounded-lg border text-body appearance-none
            focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
            bg-white text-ink-soft
            ${error ? 'border-danger' : 'border-border-strong'}`}
        >
          <option value="">Select your country</option>
          {countries.map(country => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.name}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <ChevronDown className="h-5 w-5 text-ink-muted" />
        </div>
      </div>
      {error && (
        <p className="text-small text-danger flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}
      {value && (
        <p className="text-small text-ink-muted">
          This will set your default currency and measurement units
        </p>
      )}
    </div>
  );
};

export const RegisterForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    user_type: 'mechanic',
    country: null,
    region_code: '',
    preferred_language: 'en'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { register } = useAuth();
  const navigate = useNavigate();

  // Tagged links from crowmobile.com's search pages; see signupAttribution.js.
  useEffect(() => {
    captureSignupSource(window.location.search);
  }, []);

  const userTypes = [
    {
      value: 'mechanic',
      label: 'Mechanic',
      icon: UserCheck,
      description: 'Professional repair shop or service center'
    },
    {
      value: 'consumer',
      label: 'Car Owner',
      icon: Car,
      description: 'Individual car owner or enthusiast'
    },
    {
      value: 'fleetmanager',
      label: 'Fleet Manager',
      icon: Truck,
      description: 'Manage multiple vehicles for your business'
    }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Must contain at least one letter and one number';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.user_type) newErrors.user_type = 'User type is required';
    if (!formData.country) newErrors.country = 'Country is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { confirmPassword, country, ...registrationData } = formData;
      const finalData = {
        ...registrationData,
        region_code: country.region,
        country: country.name,
        preferred_language: getDefaultLanguage(country.region)
      };
      const newUser = await register(finalData);
      recordSignupComplete(newUser?.id);
      localStorage.setItem('userRegion', country.region);
      localStorage.setItem('userCountry', country.name);
      // Mechanics start a no-card 30-day trial at registration, so drop them
      // straight into the app — no card, no plan-selection wall. Everyone else
      // continues to plan selection; newRegistration suppresses the
      // "trial has ended" banner for those fresh signups.
      if (finalData.user_type === 'mechanic') {
        // Meta conversion signal: a mechanic completed registration.
        trackEvent('CompleteRegistration', { content_name: 'mechanic_trial' });
        navigate('/');
      } else {
        navigate('/subscription-selection', { state: { newRegistration: true } });
      }
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultLanguage = (region) => {
    const map = { 'US': 'en', 'CA': 'en', 'MX': 'es', 'JP': 'ja', 'EU-DE': 'de', 'EU-FR': 'fr', 'EU-ES': 'es', 'EU-IT': 'it', 'GB': 'en', 'AU': 'en', 'NZ': 'en', 'CN': 'zh-CN', 'KR': 'ko' };
    return map[region] || 'en';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleCountryChange = (country) => {
    setFormData(prev => ({ ...prev, country }));
    if (errors.country) setErrors(prev => ({ ...prev, country: null }));
  };

  return (
    <AuthLayout title="Create your account" subtitle="Join CROW and take control of your automotive needs">
      <form className="space-y-5" onSubmit={handleSubmit}>
        {/* User Type Selection */}
        <div className="space-y-1.5">
          <label className="block text-small font-semibold text-ink-soft">I am a...</label>
          <div className="grid grid-cols-1 gap-2">
            {userTypes.map((type) => {
              const Icon = type.icon;
              const isSelected = formData.user_type === type.value;
              return (
                <label
                  key={type.value}
                  className={`relative flex items-center p-3 border rounded-lg cursor-pointer transition-colors min-h-touch
                    ${isSelected
                      ? 'border-accent bg-accent-tint'
                      : 'border-border-default hover:border-border-strong'}`}
                >
                  <input
                    type="radio"
                    name="user_type"
                    value={type.value}
                    checked={isSelected}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${isSelected ? 'text-accent' : 'text-ink-muted'}`} />
                  <div className="flex-1">
                    <div className={`text-body-strong ${isSelected ? 'text-accent' : 'text-ink-soft'}`}>
                      {type.label}
                    </div>
                    <div className="text-small text-ink-muted">{type.description}</div>
                  </div>
                </label>
              );
            })}
          </div>
          {errors.user_type && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.user_type}
            </p>
          )}
        </div>

        {/* Full Name */}
        <div className="space-y-1.5">
          <label htmlFor="full_name" className="block text-small font-semibold text-ink-soft">Full Name</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-ink-muted" />
            </div>
            <input
              id="full_name" name="full_name" type="text" autoComplete="name" required
              value={formData.full_name} onChange={handleChange}
              className={`w-full min-h-touch pl-10 pr-3 rounded-lg border text-body
                focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
                bg-white text-ink-soft placeholder-ink-muted
                ${errors.full_name ? 'border-danger' : 'border-border-strong'}`}
              placeholder="Enter your full name"
            />
          </div>
          {errors.full_name && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />{errors.full_name}
            </p>
          )}
        </div>

        {/* Country */}
        <CountrySelector
          value={formData.country?.code || ''}
          onChange={handleCountryChange}
          error={errors.country}
        />

        {/* Email */}
        <div className="space-y-1.5">
          <label htmlFor="email" className="block text-small font-semibold text-ink-soft">Email address</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-ink-muted" />
            </div>
            <input
              id="email" name="email" type="email" autoComplete="email" required
              value={formData.email} onChange={handleChange}
              className={`w-full min-h-touch pl-10 pr-3 rounded-lg border text-body
                focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
                bg-white text-ink-soft placeholder-ink-muted
                ${errors.email ? 'border-danger' : 'border-border-strong'}`}
              placeholder="Enter your email"
            />
          </div>
          {errors.email && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />{errors.email}
            </p>
          )}
        </div>

        {/* Password */}
        <div className="space-y-1.5">
          <label htmlFor="password" className="block text-small font-semibold text-ink-soft">Password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-ink-muted" />
            </div>
            <input
              id="password" name="password" type={showPassword ? 'text' : 'password'}
              autoComplete="new-password" required
              value={formData.password} onChange={handleChange}
              className={`w-full min-h-touch pl-10 pr-10 rounded-lg border text-body
                focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
                bg-white text-ink-soft placeholder-ink-muted
                ${errors.password ? 'border-danger' : 'border-border-strong'}`}
              placeholder="Create a password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center min-w-touch justify-center"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5 text-ink-muted" /> : <Eye className="h-5 w-5 text-ink-muted" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />{errors.password}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-1.5">
          <label htmlFor="confirmPassword" className="block text-small font-semibold text-ink-soft">Confirm Password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-ink-muted" />
            </div>
            <input
              id="confirmPassword" name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password" required
              value={formData.confirmPassword} onChange={handleChange}
              className={`w-full min-h-touch pl-10 pr-10 rounded-lg border text-body
                focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
                bg-white text-ink-soft placeholder-ink-muted
                ${errors.confirmPassword ? 'border-danger' : 'border-border-strong'}`}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center min-w-touch justify-center"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5 text-ink-muted" /> : <Eye className="h-5 w-5 text-ink-muted" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />{errors.confirmPassword}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full min-h-touch flex items-center justify-center rounded-lg
            bg-accent text-white font-medium text-body
            hover:bg-accent-hover transition-colors
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : (
            // Mechanics skip plan selection (no-card 30-day trial, see
            // handleSubmit), so promising it to them was wrong.
            formData.user_type === 'mechanic' ? 'Create account' : 'Continue to plan selection'
          )}
        </button>

        <p className="text-center text-body text-ink-muted">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-accent hover:text-accent-hover font-medium transition-colors"
          >
            Sign in
          </button>
        </p>
      </form>
    </AuthLayout>
  );
};

export default RegisterForm;
